import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ALL_VEHICLES_SELECT_QUERY,
  type AllVehiclesSelectQuery,
  type AllVehiclesQueryVariables,
  type VehicleSelectNode,
} from '@/modules/vehicles'
import { Combobox, type ComboboxItem } from '../combobox'
import { type VehicleSelectProps } from './types'

const VehicleSelect = (allProps: VehicleSelectProps) => {
  const { currentlyOwned, excludeVehicleIds = [], ...comboboxProps } = allProps

  const { data, loading, error } = useQuery<
    AllVehiclesSelectQuery,
    AllVehiclesQueryVariables
  >(ALL_VEHICLES_SELECT_QUERY, {
    variables: {
      orderBy: 'registrationNumber',
      currentlyOwned,
    },
  })

  const excludedIdSet = useMemo(
    () => new Set(excludeVehicleIds),
    [excludeVehicleIds]
  )

  const vehicles = useMemo<VehicleSelectNode[]>(
    () => (data?.allVehicles.edges ?? []).map(({ node }) => node),
    [data?.allVehicles.edges]
  )

  const filteredVehicles = useMemo(() => {
    // Exclusions are applied to both displayed items and returned selections.
    if (excludedIdSet.size === 0) return vehicles
    return vehicles.filter(vehicle => !excludedIdSet.has(vehicle.id))
  }, [vehicles, excludedIdSet])

  // Enables O(1) lookup and preserves selected-id order in multi-select mapping.
  const vehiclesById = useMemo(
    () => new Map(filteredVehicles.map(vehicle => [vehicle.id, vehicle])),
    [filteredVehicles]
  )

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      filteredVehicles.map(vehicle => ({
        value: vehicle.id,
        label: vehicle.registrationNumber || vehicle.vin || vehicle.id,
        // Allow searching by both registration number and VIN.
        filterValue: [vehicle.registrationNumber, vehicle.vin]
          .filter(Boolean)
          .join(' '),
      })),
    [filteredVehicles]
  )

  const noResultsText = loading
    ? 'Loading vehicles...'
    : error
    ? 'Could not load vehicles'
    : undefined

  if (comboboxProps.mode === 'multiple') {
    return (
      <Combobox
        placeholder="Select vehicles"
        {...comboboxProps}
        mode="multiple"
        items={items}
        noResultsText={noResultsText}
        onChange={ids =>
          comboboxProps.onChange(
            ids
              .map(id => vehiclesById.get(id))
              .filter((vehicle): vehicle is VehicleSelectNode => !!vehicle)
          )
        }
      />
    )
  }

  return (
    <Combobox
      placeholder="Select vehicle"
      {...comboboxProps}
      mode="single"
      items={items}
      noResultsText={noResultsText}
      onChange={id => comboboxProps.onChange(vehiclesById.get(id) ?? null)}
    />
  )
}

export default VehicleSelect
